// src/hooks/useUserPreferences.ts
//
// Reads + writes user preferences stored on the user doc at
// `users/{uid}.preferences`. Currently:
//
//   Discover:
//     - discoverRadiusKm: max distance for Discover feed items (km)
//     - distanceUnit: 'km' | 'mi' for display
//
//   Map exploration (see MAP_NOTES.md):
//     - mapMinRating:       min Google rating to show (default 3.0)
//     - mapMinReviews:      min review count (default 10, filters
//                           freshly-spammed listings)
//     - mapDefaultRadiusM:  default search radius in meters (1500)
//
// Defaults are returned synchronously so the UI can render before
// the network round-trip; once the user doc loads the real values
// flow in.
//
// The interface name `DiscoverPreferences` is historical — when
// added it only held discover-related keys. It's now the union of
// all per-user prefs and is exported as `UserPreferences` going
// forward; the old name remains as an alias so existing imports
// don't break.

import { useEffect, useState, useCallback } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

export interface UserPreferences {
  // ── Discover ─────────────────────────────────────────────
  discoverRadiusKm: number;
  distanceUnit: 'km' | 'mi';

  // ── Map exploration ──────────────────────────────────────
  /** Minimum Google rating to surface in map exploration results.
   *  3.0 catches everyone above mediocre without being snobby. */
  mapMinRating: number;
  /** Minimum number of Google reviews. Filters freshly-spammed
   *  listings and one-off home addresses incorrectly tagged as
   *  businesses. 10 is conservative; raise if results feel noisy. */
  mapMinReviews: number;
  /** Default search radius in meters when the user's query
   *  doesn't imply a different range ("walking distance" → 800,
   *  "in the city" → 5000, etc — those overrides come from the
   *  intent extractor in createMapExploration.ts). 1500m ≈ 18-20
   *  min walk. */
  mapDefaultRadiusM: number;
}

/** Backward-compat alias — preserved so existing call sites keep
 *  importing the old name. New code should prefer UserPreferences. */
export type DiscoverPreferences = UserPreferences;

const DEFAULTS: UserPreferences = {
  discoverRadiusKm: 3,
  // Default unit by browser locale: imperial for en-US, metric otherwise.
  distanceUnit:
    typeof navigator !== 'undefined' &&
    (navigator.language || '').toLowerCase().startsWith('en-us')
      ? 'mi'
      : 'km',
  mapMinRating: 3.0,
  mapMinReviews: 10,
  mapDefaultRadiusM: 1500,
};

export const RADIUS_MIN_KM = 0.5;
export const RADIUS_MAX_KM = 50;

// Map-pref bounds. The validation in the snapshot reader uses these
// — values outside fall back to the default. The settings UI (when
// it lands) will use the same constants for slider min/max.
export const MAP_MIN_RATING_FLOOR = 0;
export const MAP_MIN_RATING_CEIL = 5;
export const MAP_MIN_REVIEWS_FLOOR = 0;
export const MAP_MIN_REVIEWS_CEIL = 10000;
export const MAP_RADIUS_FLOOR_M = 100;
export const MAP_RADIUS_CEIL_M = 20000;

export function useUserPreferences() {
  const [prefs, setPrefs] = useState<UserPreferences>(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setLoaded(true);
      return;
    }
    const ref = doc(db, 'users', user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() as any;
        const p = data?.preferences || {};
        const next: UserPreferences = {
          discoverRadiusKm:
            typeof p.discoverRadiusKm === 'number' &&
            p.discoverRadiusKm >= RADIUS_MIN_KM &&
            p.discoverRadiusKm <= RADIUS_MAX_KM
              ? p.discoverRadiusKm
              : DEFAULTS.discoverRadiusKm,
          distanceUnit:
            p.distanceUnit === 'mi'
              ? 'mi'
              : p.distanceUnit === 'km'
              ? 'km'
              : DEFAULTS.distanceUnit,
          mapMinRating:
            typeof p.mapMinRating === 'number' &&
            p.mapMinRating >= MAP_MIN_RATING_FLOOR &&
            p.mapMinRating <= MAP_MIN_RATING_CEIL
              ? p.mapMinRating
              : DEFAULTS.mapMinRating,
          mapMinReviews:
            typeof p.mapMinReviews === 'number' &&
            p.mapMinReviews >= MAP_MIN_REVIEWS_FLOOR &&
            p.mapMinReviews <= MAP_MIN_REVIEWS_CEIL
              ? p.mapMinReviews
              : DEFAULTS.mapMinReviews,
          mapDefaultRadiusM:
            typeof p.mapDefaultRadiusM === 'number' &&
            p.mapDefaultRadiusM >= MAP_RADIUS_FLOOR_M &&
            p.mapDefaultRadiusM <= MAP_RADIUS_CEIL_M
              ? p.mapDefaultRadiusM
              : DEFAULTS.mapDefaultRadiusM,
        };
        setPrefs(next);
        setLoaded(true);
      },
      (err) => {
        console.warn('useUserPreferences snapshot error:', err);
        setLoaded(true);
      }
    );
    return unsub;
  }, []);

  const update = useCallback(
    async (patch: Partial<UserPreferences>) => {
      const user = auth.currentUser;
      if (!user) return;
      // Optimistic local update
      setPrefs((cur) => ({ ...cur, ...patch }));
      try {
        await setDoc(
          doc(db, 'users', user.uid),
          { preferences: { ...prefs, ...patch } },
          { merge: true }
        );
      } catch (err) {
        console.warn('useUserPreferences update failed:', err);
      }
    },
    [prefs]
  );

  return {
    prefs,
    loaded,
    update,
  };
}

/** Display helpers — keep storage in km, convert at the edges. */
export function kmToDisplay(km: number, unit: 'km' | 'mi'): number {
  if (unit === 'mi') return km * 0.621371;
  return km;
}

export function displayToKm(value: number, unit: 'km' | 'mi'): number {
  if (unit === 'mi') return value / 0.621371;
  return value;
}

export function formatDistance(
  meters: number,
  unit: 'km' | 'mi'
): string {
  if (unit === 'mi') {
    const mi = meters / 1609.34;
    if (mi < 0.1) return `${Math.round(meters * 3.28084)} ft`;
    return `${mi.toFixed(mi < 1 ? 2 : 1)} mi`;
  }
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(meters < 10000 ? 1 : 0)} km`;
}
